// 🔧 settings_volunteers.js
// ✅ Purpose: Admin-only Volunteer Content Settings page
// ✅ Features:
// - Standalone replacement for old Volunteers tab
// - Contact note for volunteers
// - Volunteer goodies/perks section
// - Thank-you message shown at bottom of volunteer page
// - Loads/saves /events/{eventId}/settings/voting
// - Uses standard admin layout
// - Shows success/error notifications at the top of the page
// - Auto-clears notifications after 20 seconds

import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useRole } from "../../role_context";
import { useIsEventManager } from "../../hooks/use_is_event_manager";
import FullPageSpinner from "../../components/FullPageSpinner";
import { showStatus } from "../../utils/status_helpers";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const SettingsVolunteers = () => {
  const { eventId, eventLoading, ready } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole } = useRole();
  const isManager = useIsEventManager(eventId);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [contactNote, setContactNote] = useState("");
  const [volunteerGoodies, setVolunteerGoodies] = useState("");
  const [volunteerThanks, setVolunteerThanks] = useState("");
  const [saving, setSaving] = useState(false);

  const canAccess =
    ["admin", "manager"].includes(role) ||
    eventRole === "manager" ||
    isManager;

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!eventId) return;

      try {
        console.log("🙋 Loading volunteer content settings for event:", eventId);

        const snap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const data = snap.exists() ? snap.data() : {};

        setSettings(data);
        setContactNote(data.contactNote || "");
        setVolunteerGoodies(data.volunteerGoodies || "");
        setVolunteerThanks(data.volunteerThanks || "");
      } catch (err) {
        console.error("❌ Failed to load volunteer content settings:", err);

        setStatus({
          type: "error",
          message: "❌ Failed to load volunteer content settings.",
        });
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [eventId]);

  const handleSave = async () => {
    if (!eventId) return;

    setSaving(true);

    try {
      const update = {
        contactNote,
        volunteerGoodies,
        volunteerThanks,
        lastSaved: Timestamp.now(),
      };

      await setDoc(
        doc(db, "events", eventId, "settings", "voting"),
        update,
        { merge: true }
      );

      setSettings((prev) => ({
        ...prev,
        ...update,
      }));

      showStatus(setStatus, {
        type: "success",
        message: "Volunteer settings saved successfully.",
      });
    } catch (err) {
      console.error("❌ Failed to save volunteer settings:", err);

      showStatus(setStatus, {
        type: "error",
        message: "Failed to save volunteer settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  const lastSavedText = settings?.lastSaved?.toDate?.()
    ? settings.lastSaved.toDate().toLocaleString()
    : "Never";


  if (!ready || eventLoading || settingsLoading || isManager === null) {
    return <FullPageSpinner message="Loading volunteer content..." />;
  }

  if (!canAccess) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          You do not have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">
              🙋 Volunteer Content
            </h1>

            <p className="lead text-muted mb-0">
              Configure volunteer messaging, perks, and thank-you content shown throughout the volunteer experience.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Volunteer Content"}
            </button>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}


        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>📞 Contact the Event Promoter</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={contactNote}
              onChange={setContactNote}
            />

            <div className="form-text mt-3">
              This message appears on the volunteer page to help volunteers know who to contact with questions.
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🎁 Volunteer Goodies</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={volunteerGoodies}
              onChange={setVolunteerGoodies}
            />

            <div className="form-text mt-3">
              Describe perks such as meals, shirts, event access, gifts, or volunteer incentives.
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>🙏 Volunteer Thank You Message</strong>
          </div>

          <div className="card-body">
            <ReactQuill
              theme="snow"
              value={volunteerThanks}
              onChange={setVolunteerThanks}
            />

            <div className="form-text mt-3">
              Displayed at the end of the volunteer experience as a thank-you message.
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-4">
          <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <strong>Save Volunteer Content</strong>

              <div className="small text-muted">
                Last saved: {lastSavedText}
              </div>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "💾 Save Volunteer Content"}
            </button>
          </div>
        </div>

      </div>
    </section>
  );

};

export default SettingsVolunteers;